import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { emitCacheUpdate } from '../utils/cacheEvents';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://oauth.remote-assistant.ai';

const getAuthHeaders = async () => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error('No authentication token found');
  }
  return { Authorization: `Bearer ${token}` };
};

const bookingService = {
  // Staff
  listStaff: async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/staff`, { headers });
    return response.data.staff;
  },

  createStaff: async (data) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE}/oauth/staff`, data, { headers });
    return response.data.staff;
  },

  updateStaff: async (staffId, data) => {
    const headers = await getAuthHeaders();
    const response = await axios.put(`${API_BASE}/oauth/staff/${staffId}`, data, { headers });
    return response.data.staff;
  },

  deleteStaff: async (staffId) => {
    const headers = await getAuthHeaders();
    await axios.delete(`${API_BASE}/oauth/staff/${staffId}`, { headers });
  },

  exportStaffCSV: async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/staff/export`, { headers });
    return response.data;
  },

  importStaffCSV: async (csvData) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE}/oauth/staff/import`, 
      { csv_data: csvData }, 
      { headers }
    );
    return response.data;
  },

  // Services
  listServices: async (businessId = null) => {
    const headers = await getAuthHeaders();
    const url = businessId 
      ? `${API_BASE}/oauth/services?business_id=${businessId}`
      : `${API_BASE}/oauth/services`;
    const response = await axios.get(url, { headers });
    return response.data.services;
  },

  createService: async (data) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE}/oauth/services`, data, { headers });
    return response.data.service;
  },

  updateService: async (serviceId, data) => {
    const headers = await getAuthHeaders();
    const response = await axios.put(`${API_BASE}/oauth/services/${serviceId}`, data, { headers });
    return response.data.service;
  },

  deleteService: async (serviceId) => {
    const headers = await getAuthHeaders();
    await axios.delete(`${API_BASE}/oauth/services/${serviceId}`, { headers });
  },

  exportServicesCSV: async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/services/export`, { headers });
    return response.data;
  },

  importServicesCSV: async (csvData) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE}/oauth/services/import`, 
      { csv_data: csvData }, 
      { headers }
    );
    return response.data;
  },

  // Bookings
  listBusinessBookings: async (params) => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/bookings/calendar`, {
      headers,
      params
    });
    return response.data.bookings;
  },

  createBooking: async (data) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE}/oauth/bookings`, data, { headers });
    return response.data;
  },

  cancelBooking: async (bookingId, businessId) => {
    const headers = await getAuthHeaders();
    await axios.delete(`${API_BASE}/oauth/bookings/${bookingId}`, {
      headers,
      data: { business_id: businessId }
    });
  },

  getAvailability: async (businessId, params) => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/availability`, {
      headers,
      params: { business_id: businessId, ...params }
    });
    return response.data;
  },

  // Staff Availability
  listStaffAvailability: async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/staff-availability`, { headers });
    return response.data.availability;
  },

  getStaffAvailability: async (staffId) => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/oauth/staff-availability/${staffId}`, { headers });
    return response.data.availability;
  },

  createStaffAvailability: async (data) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE}/oauth/staff-availability`, data, { headers });
    return response.data.availability;
  },

  updateStaffAvailability: async (staffId, data) => {
    const headers = await getAuthHeaders();
    const response = await axios.put(`${API_BASE}/oauth/staff-availability/${staffId}`, data, { headers });
    return response.data.availability;
  },

  deleteStaffAvailability: async (staffId) => {
    const headers = await getAuthHeaders();
    await axios.delete(`${API_BASE}/oauth/staff-availability/${staffId}`, { headers });
  }
};

export default bookingService;
